/**
 * SDK Error Types
 *
 * Typed error classes for SDK operations. No silent fallbacks —
 * all errors propagate immediately with full context.
 */

export class SDKError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SDKError';
  }
}

export class APIError extends SDKError {
  readonly status: number;
  readonly statusText: string;
  readonly code: string;
  readonly detail: unknown;
  /**
   * The `auth_reason` the backend puts on every 401 it adjudicates
   * (`go/internal/api/auth_middleware.go` — one mint site, so the wire value and
   * the `auth.jwt.rejected{reason}` metric cannot disagree).
   *
   * It names WHICH LAYER failed, which `code` alone cannot: "I sent no
   * credential" (`missing_token`, LOCAL, says nothing about the session) and "my
   * credential is dead" (`bad_signature`, GLOBAL, terminal) were byte-identical
   * before it existed, which is why every client resorted to matching message
   * TEXT and one of them produced an infinite sign-in loop.
   *
   * Null on any response that carries no `auth_reason`. Consumers MUST read this
   * through `classifyVerdict` rather than switching on it directly — a 401 that
   * names no reason is not an adjudication of the session and must not move a
   * credential holder (see `src/auth/authState.ts`).
   */
  readonly authReason: string | null;
  /** Winning workspace operation for an OPERATION_IN_PROGRESS conflict. */
  readonly operationId: string | null;
  readonly operationKind: string | null;

  constructor(status: number, statusText: string, body: unknown) {
    const code = extractErrorCode(body) ?? `HTTP_${status}`;
    // Prefer the backend's human-readable message (Huma serializes
    // huma.ErrorNNN(msg) into a top-level `detail` string) so surfaced
    // errors read "name already exists" rather than "HTTP_409". Fall back to
    // the machine code only when no message is present.
    const humanMessage = extractErrorMessage(body);
    super(`${status} ${statusText}: ${humanMessage ?? code}`);
    this.name = 'APIError';
    this.status = status;
    this.statusText = statusText;
    this.code = code;
    this.detail = body;
    this.authReason = extractAuthReason(body);
    this.operationId = extractNonEmptyString(body, 'operation_id');
    this.operationKind = extractNonEmptyString(body, 'operation_kind');
  }
}

function extractNonEmptyString(body: unknown, key: string): string | null {
  if (!body || typeof body !== 'object') return null;
  const value = (body as Record<string, unknown>)[key];
  return typeof value === 'string' && value !== '' ? value : null;
}

export class NetworkError extends SDKError {
  readonly url: string;

  readonly cause?: Error;

  constructor(message: string, url: string, cause?: Error) {
    super(message);
    this.name = 'NetworkError';
    this.url = url;
    this.cause = cause;
  }
}

export class TimeoutError extends SDKError {
  readonly timeoutMs: number;
  readonly url: string;

  readonly cause?: Error;

  constructor(timeoutMs: number, url: string, cause?: Error) {
    super(`Request timed out after ${timeoutMs}ms: ${url}`);
    this.name = 'TimeoutError';
    this.timeoutMs = timeoutMs;
    this.url = url;
    this.cause = cause;
  }
}

export class RequestCancelledError extends SDKError {
  readonly url: string;

  constructor(url: string) {
    super(`Request cancelled: ${url}`);
    this.name = 'RequestCancelledError';
    this.url = url;
  }
}

/**
 * A CONFIGURED `AuthProvider` returned no token for a request that was not
 * declared `unauthenticated`.
 *
 * This used to be silent: `if (token !== null) headers['Authorization'] = …`
 * simply omitted the header and let the request go out bare. The resulting 401
 * was then indistinguishable from "your session is dead" — which is exactly how
 * `/desktop-auth` sent handoff calls with no auth header at all, misread the
 * 401 as a dead session, and produced an infinite sign-in loop.
 *
 * Throwing here makes the two facts structurally distinct: this error means
 * "the client has no credential to send" (recovery: acquire one), while a 401
 * means "the backend adjudicated the credential I did send".
 */
export class MissingCredentialError extends SDKError {
  readonly url: string;

  constructor(url: string) {
    super(
      `No credential available for ${url}: the configured AuthProvider returned null. ` +
        `If this route is legitimately unauthenticated, pass { unauthenticated: true } — ` +
        `omitting the Authorization header silently turns a client-side gap into an ` +
        `unattributable 401.`
    );
    this.name = 'MissingCredentialError';
    this.url = url;
  }
}

/**
 * A persona `agent_config` was missing fields the backend requires
 * (persona ADR-020). Thrown SYNCHRONOUSLY, before any network call.
 *
 * Every missing key is reported at once, deliberately: an agent that learns
 * the contract one 422 at a time pays a round trip per field, which is how a
 * config naming no provider, model or process reached the database and
 * produced agents that died on their first turn.
 *
 * The SDK checks SHAPE only — which keys must be present. Which models and
 * processes actually exist stays the backend's sole authority.
 */
export class PersonaConfigIncompleteError extends SDKError {
  readonly missing: string[];

  constructor(method: string, missing: string[]) {
    super(
      `${method}: agent_config is incomplete — ${missing.join('; ')}. ` +
        `A persona that names no model or process cannot execute.`
    );
    this.name = 'PersonaConfigIncompleteError';
    this.missing = missing;
  }
}

/**
 * An attached image's read copy has expired, so its bytes can no longer be
 * shown. The image's metadata is still readable; re-attaching restores it.
 */
export class SessionImageExpiredError extends SDKError {
  readonly imageId: string;

  constructor(imageId: string) {
    super(`session image ${imageId} has expired; re-attach it to see it again`);
    this.name = 'SessionImageExpiredError';
    this.imageId = imageId;
  }
}

/**
 * A non-2xx from the ShogunChop daemon's loopback HTTP API.
 *
 * The daemon is not the cloud API — it has its own base URL and no Huma error
 * envelope — so its failures are typed separately from `APIError`. What matters
 * is that `status` is carried STRUCTURALLY: callers that legitimately handle a
 * specific status (`authInfo` treating 401 as "no tokens yet") read the number.
 * Matching `message.includes('401')` is the exact anti-pattern the auth arc
 * exists to delete, and it misfires on any body that happens to contain "401".
 */
export class ShogunChopHTTPError extends SDKError {
  readonly status: number;
  readonly statusText: string;
  readonly url: string;
  readonly body: unknown;

  constructor(status: number, statusText: string, url: string, body: unknown) {
    super(
      `ShogunChop API error: ${status} ${statusText} (${url}) — ` +
        `${typeof body === 'string' ? body : JSON.stringify(body)}`
    );
    this.name = 'ShogunChopHTTPError';
    this.status = status;
    this.statusText = statusText;
    this.url = url;
    this.body = body;
  }
}

/**
 * A JWT could not be decoded — not three segments, invalid base64url, or a
 * payload that is not a JSON object.
 *
 * Typed rather than a null return so "this token is garbage" never reads
 * identically to "this token has no such claim" (`.claude/rules/no-fallbacks.md`).
 * A caller holding possibly-stale storage catches THIS type specifically.
 */
export class MalformedJwtError extends SDKError {
  constructor(detail: string) {
    super(`Malformed JWT: ${detail}`);
    this.name = 'MalformedJwtError';
  }
}

export function isAPIError(error: unknown): error is APIError {
  return error instanceof APIError;
}

export function isNotFound(error: unknown): error is APIError {
  return error instanceof APIError && error.status === 404;
}

export function isConflict(error: unknown): error is APIError {
  return error instanceof APIError && error.status === 409;
}

export function isForbidden(error: unknown): error is APIError {
  return error instanceof APIError && error.status === 403;
}

export function isNetworkError(error: unknown): error is NetworkError {
  return error instanceof NetworkError;
}

export function isTimeoutError(error: unknown): error is TimeoutError {
  return error instanceof TimeoutError;
}

function extractErrorCode(body: unknown): string | undefined {
  if (body && typeof body === 'object') {
    // Huma error format: { errors: [{ code: "..." }] }
    const asRecord = body as Record<string, unknown>;
    if (Array.isArray(asRecord.errors) && asRecord.errors.length > 0) {
      const first = asRecord.errors[0] as Record<string, unknown>;
      if (typeof first.code === 'string') return first.code;
    }
    // Alternative format: { detail: [{ code: "..." }] }
    if (Array.isArray(asRecord.detail) && asRecord.detail.length > 0) {
      const first = asRecord.detail[0] as Record<string, unknown>;
      if (typeof first.code === 'string') return first.code;
    }
    // Simple format: { code: "..." }
    if (typeof asRecord.code === 'string') return asRecord.code;
    // Temporal/domain error format: { error_code: "..." } — the canonical wire
    // field the Go API's TemporalErrorResponse serializes an ApplicationError
    // type under (go/internal/api/errors.go `json:"error_code"`). Without this
    // arm a typed domain code (e.g. LAST_OWNER_CANNOT_BE_REVOKED) never reaches
    // APIError.code, so resource-layer typed-error mapping falls back to HTTP_NNN.
    if (typeof asRecord.error_code === 'string') return asRecord.error_code;
  }
  return undefined;
}

/**
 * Extract the backend's `auth_reason` off a 401 body.
 *
 * Written by exactly one site on the middleware lane (`writeAuthReject`) and by
 * the SESSION_NOT_ACTIVATED arm of `mapAuthErrorBody` on the handler lane. It is
 * a plain top-level string; anything else is treated as absent, because a
 * non-string here is a wire-contract break and `classifyVerdict` must reach the
 * `unknown` verdict for it rather than guess.
 */
function extractAuthReason(body: unknown): string | null {
  if (body && typeof body === 'object') {
    const value = (body as Record<string, unknown>).auth_reason;
    if (typeof value === 'string' && value !== '') return value;
  }
  return null;
}

/**
 * Extract a human-readable message from an error body. Huma serializes
 * `huma.ErrorNNN(msg)` into a top-level `detail` string; our structured
 * activity errors also carry a `message` field. Array-shaped `detail`
 * (validation errors) is left to `extractErrorCode` — this only pulls a
 * plain human sentence.
 */
function extractErrorMessage(body: unknown): string | undefined {
  if (body && typeof body === 'object') {
    const asRecord = body as Record<string, unknown>;
    if (typeof asRecord.detail === 'string' && asRecord.detail.trim() !== '') {
      return asRecord.detail;
    }
    if (typeof asRecord.message === 'string' && asRecord.message.trim() !== '') {
      return asRecord.message;
    }
    if (Array.isArray(asRecord.errors) && asRecord.errors.length > 0) {
      const first = asRecord.errors[0] as Record<string, unknown>;
      if (typeof first.message === 'string' && first.message.trim() !== '') {
        return first.message;
      }
    }
  }
  return undefined;
}
